import { base64ToBytes, bytesToBase64, bytesToUtf8, toArrayBuffer, utf8ToBytes } from '../utils/encoding';
import { getHistorySecretPlatformPolicy, isHistorySecretProfileAllowed } from './platformPolicy';
import { saveHistorySecretRecords } from './storage';
import type { HistorySecretProfile, HistorySecretRecord, LocalHistoryCiphertext } from './types';

const HISTORY_CACHE_FORMAT_VERSION = 1;
const HISTORY_CACHE_SALT = utf8ToBytes('circlus/history-cache/salt/v1');
const HISTORY_CACHE_INFO_PREFIX = 'circlus/history-cache/direct/v1';

function buildHistorySecretId(params: {
  identityId: string;
  serverId?: string | null;
  profile: HistorySecretProfile;
  version: number;
}): string {
  return [
    'history',
    params.identityId.trim(),
    (params.serverId || '').trim() || 'local',
    params.profile,
    `v${params.version}`
  ].join(':');
}

async function deriveHistoryCacheKey(params: {
  identityId: string;
  serverId?: string | null;
  ecdhScalar: Uint8Array;
  profile?: HistorySecretProfile;
}): Promise<{ key: CryptoKey; record: HistorySecretRecord } | null> {
  if (!crypto?.subtle) return null;
  const policy = getHistorySecretPlatformPolicy();
  const profile = params.profile || policy.preferredProfile;
  if (!isHistorySecretProfileAllowed(profile)) return null;
  if (profile !== 'classic') return null;

  const identityId = params.identityId.trim();
  if (!identityId || !params.ecdhScalar?.length) return null;
  const serverId = (params.serverId || '').trim() || null;
  const version = HISTORY_CACHE_FORMAT_VERSION;
  const secretId = buildHistorySecretId({ identityId, serverId, profile, version });
  const record: HistorySecretRecord = {
    secretId,
    profile,
    platform: policy.platform,
    scope: { identityId, serverId },
    version,
    status: 'active',
    createdAt: 0
  };

  const hkdfKey = await crypto.subtle.importKey(
    'raw',
    toArrayBuffer(params.ecdhScalar),
    'HKDF',
    false,
    ['deriveKey']
  );

  const info = utf8ToBytes(`${HISTORY_CACHE_INFO_PREFIX}:${identityId}:${serverId || 'local'}:${profile}:v${version}`);
  const key = await crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: toArrayBuffer(HISTORY_CACHE_SALT),
      info: toArrayBuffer(info)
    },
    hkdfKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );

  return { key, record };
}

export async function encryptDirectHistoryCache(params: {
  plaintext: string;
  identityId: string;
  serverId?: string | null;
  ecdhScalar: Uint8Array;
  profile?: HistorySecretProfile;
}): Promise<LocalHistoryCiphertext | null> {
  const derived = await deriveHistoryCacheKey(params);
  if (!derived) return null;

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(iv) },
    derived.key,
    toArrayBuffer(utf8ToBytes(params.plaintext))
  );

  await saveHistorySecretRecords([{
    ...derived.record,
    createdAt: Date.now()
  }]);

  return {
    encryption: {
      format: 'identity-cache-v1',
      secretId: derived.record.secretId,
      profile: derived.record.profile,
      version: derived.record.version
    },
    ciphertextBase64: bytesToBase64(new Uint8Array(encrypted)),
    nonceBase64: bytesToBase64(iv),
    algorithm: 'AES-GCM-256',
    source: 'direct-chat',
    protocolEpoch: null
  };
}

export async function decryptDirectHistoryCache(params: {
  payload: LocalHistoryCiphertext;
  identityId: string;
  serverId?: string | null;
  ecdhScalar: Uint8Array;
}): Promise<string | null> {
  const payload = params.payload;
  if (payload.source !== 'direct-chat') return null;
  const derived = await deriveHistoryCacheKey({
    identityId: params.identityId,
    serverId: params.serverId,
    ecdhScalar: params.ecdhScalar,
    profile: payload.encryption.profile
  });
  if (!derived) return null;
  if (derived.record.secretId !== payload.encryption.secretId) return null;

  try {
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: toArrayBuffer(base64ToBytes(payload.nonceBase64)) },
      derived.key,
      toArrayBuffer(base64ToBytes(payload.ciphertextBase64))
    );
    return bytesToUtf8(new Uint8Array(decrypted));
  } catch {
    return null;
  }
}
